import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db, auth } from "../firebase-config";
import {
  doc,
  getDoc,
  collection,
  getDocs,
} from "firebase/firestore";
import { Button, TextField } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PrintIcon from "@mui/icons-material/Print";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CircularProgress from "@mui/material/CircularProgress";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import "../style/Global.css";
import "../style/QuestionnaireDetail.css";

function QuestionnaireDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [questionnaire, setQuestionnaire] = useState(null);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [responses, setResponses] = useState([]);
  const [animatedChartKey, setAnimatedChartKey] = useState(0);
  const [currentDayOffset, setCurrentDayOffset] = useState(0); // 0 = today

  // Load all questionnaires
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const snapshot = await getDocs(collection(db, "Questionnaires"));
        const all = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          title: doc.data().title || "Untitled",
        }));

        const currentUser = auth.currentUser;
        if (currentUser) {
          setQuestionnaires(
            all.filter(q => 
              q.status !== "deleted" &&
              q.createdBy === currentUser.uid
            )
          );
        }
      } catch (err) {
        console.error("Error loading list:", err);
      }
    };
    fetchAll();
  }, []);

  // Fetch selected questionnaire detail
  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const docRef = doc(db, "Questionnaires", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setQuestionnaire({ id: docSnap.id, ...docSnap.data() });
          setCurrentDayOffset(0); // Reset to today when switching
        } else {
          console.error("Questionnaire not found");
        }
      } catch (err) {
        console.error("Error fetching detail:", err);
      }
    };
    if (id) {
      fetchDetail();
    }
  }, [id]);

  // Fetch responses
  const fetchResponses = useCallback(async () => {
    try {
      const docRef = doc(db, "Responses", id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setResponses(data.responses || []);
        setAnimatedChartKey(prev => prev + 1);
      } else {
        setResponses([]);
      }
    } catch (err) {
      console.error("Error fetching responses:", err);
      setResponses([]);
    }
  }, [id]);

  // Initial fetch + auto refresh
  useEffect(() => {
    fetchResponses();
    const interval = setInterval(fetchResponses, 30000);
    return () => clearInterval(interval);
  }, [fetchResponses]);

  const filteredList = questionnaires.filter(q =>
    q.title &&
    q.title.toLowerCase() !== "untitled" &&
    q.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const today = new Date();
  today.setHours(23, 59, 59, 999);

  const viewingDate = new Date();
  viewingDate.setDate(viewingDate.getDate() + currentDayOffset);
  viewingDate.setHours(23, 59, 59, 999);

  // Calculate creation day
  const creationDate = questionnaire?.createdAt
    ? new Date(questionnaire.createdAt.seconds * 1000)
    : null;

  const viewingIsAfterCreation = creationDate
    ? viewingDate >= creationDate
    : true; // allow if creation not loaded yet

  const canGoPrevious = creationDate
    ? viewingDate > creationDate
    : true; // allow if creation not loaded yet

  const canGoNext = currentDayOffset < 0;

  // Build answer frequency
  const answerMapByQuestion = {};
  responses
    .filter(entry => {
      const entryDate = new Date(entry.timestamp?.seconds * 1000);
      return entryDate <= viewingDate;
    })
    .forEach((entry) => {
      const answers = entry.answers || {};
      Object.entries(answers).forEach(([qIndex, answerText]) => {
        if (!answerMapByQuestion[qIndex]) answerMapByQuestion[qIndex] = {};
        answerMapByQuestion[qIndex][answerText] =
          (answerMapByQuestion[qIndex][answerText] || 0) + 1;
      });
    });

  const chartDataList = Object.entries(answerMapByQuestion).map(
    ([qIndex, answerFreq]) => {
      const questionText =
        questionnaire?.questions?.[parseInt(qIndex)]?.text ||
        `Question ${parseInt(qIndex) + 1}`;

      const data = Object.entries(answerFreq).map(([option, count]) => ({
        name: option,
        count,
      }));

      return {
        questionText,
        questionIdx: qIndex,
        data,
      };
    }
  );

  const handlePreviousDay = () => {
    if (canGoPrevious) {
      setCurrentDayOffset(prev => prev - 1);
    }
  };

  const handleNextDay = () => {
    if (canGoNext) {
      setCurrentDayOffset(prev => prev + 1);
    }
  };

  return (
    <div className="questionnaire-detail-container">
      {/* Sidebar */}
      <div className="sidebar">
        <Button
          variant="contained"
          onClick={() => navigate("/ViewQuestionnaire")}
          className="back-button"
        >
          Back to List
        </Button>

        <h3>Your Questionnaires</h3>

        <TextField
          fullWidth
          label="Search questionnaires..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          margin="normal"
          size="small"
        />

        <div className="questionnaire-list">
          {filteredList.map((q) => (
            <div
              key={q.id}
              className={`questionnaire-list-item ${q.id === id ? "selected" : ""}`}
              onClick={() => navigate(`/Questionnaire/${q.id}`)}
            >
              <div className="questionnaire-title">{q.title}</div>
              <div className="questionnaire-meta">
                {q.className && <span>{q.className}</span>}
                {q.status && <span className={`status-badge status-${q.status}`}>{q.status}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Panel */}
      <div className="detail-panel">
        {!questionnaire ? (
          <div className="loading-container">
            <CircularProgress />
          </div>        
        ) : (
          <>
            <div className="questionnaire-header">
              <h2>{questionnaire.title || "Untitled"}</h2>
              <div className="questionnaire-meta">
                <div className="meta-item">
                  <span className="meta-label">Class:</span>
                  <span className="meta-value">{questionnaire.className || "N/A"}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Status:</span>
                  <span className={`meta-value status-${questionnaire.status}`}>
                    {questionnaire.status || "N/A"}
                  </span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Created:</span>
                  <span className="meta-value">
                    {questionnaire.createdAt?.seconds
                      ? new Date(questionnaire.createdAt.seconds * 1000).toLocaleDateString("en-GB", {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })
                      : "N/A"}
                  </span>
                </div>
              </div>
            </div>

            <Button
              variant="contained"
              className="edit-button"
              onClick={() => navigate(`/EditQuestionnaire/${questionnaire.id}`)}
              startIcon={<EditIcon />}
            >
              Edit Questionnaire
            </Button>

            {/* Response Summary */}
            <div className="response-summary">
              <div className="response-summary-header">
                <div className="response-summary-title">
                  <h3>Response Summary</h3>
                  <Tooltip title="Reload responses">
                    <IconButton onClick={fetchResponses} size="small">
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </div>
                <Tooltip title="Print response summary">
                  <IconButton onClick={() => window.print()}>
                    <PrintIcon />
                  </IconButton>
                </Tooltip>
              </div>

              <div className="response-controls-row">
                <Button variant="outlined" onClick={handlePreviousDay} disabled={!canGoPrevious} startIcon={<ArrowBackIcon />}>
                  Previous Day
                </Button>

                <div className="response-date-center">
                  Viewing responses up to:{" "}
                  <strong>{viewingDate.toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" })}</strong>
                </div>

                <Button variant="outlined" onClick={handleNextDay} disabled={!canGoNext} startIcon={<ArrowForwardIcon />}>
                  Next Day
                </Button>
              </div>

              {chartDataList.length === 0 ? (
                <p>No responses yet for this date range.</p>
              ) : (
                <div className="chart-grid fade-in" key={animatedChartKey}>
                  {chartDataList.map((q) => (
                    <div key={`${q.questionIdx}-${animatedChartKey}`} className="chart-box">
                      <h4>{`Q${parseInt(q.questionIdx) + 1}. ${q.questionText}`}</h4>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={q.data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                          <XAxis dataKey="name" tick={{ fontSize: 12 }} tickFormatter={(value) => value.length > 10 ? `${value.substring(0, 10)}...` : value}/>
                          <YAxis allowDecimals={false} />
                          <RechartsTooltip
                            contentStyle={{
                              background: '#fff',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                            }} 
                          />
                          <Bar dataKey="count" fill="#3f51b5" radius={[4, 4, 0, 0]} animationDuration={1000}/>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </>
        )}
      </div>
    </div>
  );
}

export default QuestionnaireDetail;
